import { SseFrameParser, type SseFrame } from './sseParser';
import type { ConnectionStatus } from './streamReducer';

/**
 * `GET /api/stream` client (CONTRACTS §5).
 *
 * Implemented over `fetch` + a `ReadableStream` reader rather than the native
 * `EventSource` for one contract reason: a **forced** reconnect must carry
 * `Last-Event-ID`. `EventSource` only sends that header on reconnects it
 * initiates itself; SPEC §10's iOS-standalone recovery ("reconnect on
 * `visibilitychange` with `Last-Event-ID`") closes and reopens the stream from
 * script, where the native object would silently drop the id and the ring
 * replay with it. Everything else — `retry:` handling, id tracking, frame
 * shape — matches what `EventSource` would have done.
 */

export interface SseStreamClientOptions {
  readonly url: string;
  readonly onFrame: (frame: SseFrame) => void;
  readonly onStatus: (status: ConnectionStatus) => void;
  readonly onUnauthenticated: () => void;
  readonly fetchImpl?: typeof fetch;
}

const DEFAULT_RETRY_MS = 3000;
const MAX_RETRY_MS = 30_000;

export class SseStreamClient {
  private readonly parser = new SseFrameParser();
  private controller: AbortController | null = null;
  private stopped = true;
  private running = false;
  private attempt = 0;
  private retryMs = DEFAULT_RETRY_MS;
  private skipBackoff = false;
  private wakeTimer: ReturnType<typeof setTimeout> | null = null;
  private wake: (() => void) | null = null;

  constructor(private readonly options: SseStreamClientOptions) {}

  get lastEventId(): string | null {
    return this.parser.lastEventId;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    if (!this.running) void this.loop();
  }

  stop(): void {
    this.stopped = true;
    this.skipBackoff = false;
    this.abortCurrent();
    this.cancelWait();
  }

  /** Drop the socket and reconnect immediately, carrying `Last-Event-ID`. */
  reconnectNow(): void {
    if (this.stopped) {
      this.start();
      return;
    }
    this.skipBackoff = true;
    this.attempt = 0;
    this.abortCurrent();
    this.cancelWait();
  }

  private abortCurrent(): void {
    const controller = this.controller;
    this.controller = null;
    controller?.abort();
  }

  private cancelWait(): void {
    if (this.wakeTimer !== null) {
      clearTimeout(this.wakeTimer);
      this.wakeTimer = null;
    }
    const wake = this.wake;
    this.wake = null;
    wake?.();
  }

  private wait(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.wake = resolve;
      this.wakeTimer = setTimeout(() => {
        this.wakeTimer = null;
        this.wake = null;
        resolve();
      }, ms);
    });
  }

  private async loop(): Promise<void> {
    this.running = true;
    const doFetch = this.options.fetchImpl ?? globalThis.fetch.bind(globalThis);

    while (!this.stopped) {
      this.options.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');
      const controller = new AbortController();
      this.controller = controller;

      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        const lastId = this.parser.lastEventId;
        if (lastId !== null) headers['Last-Event-ID'] = lastId;

        const response = await doFetch(this.options.url, {
          method: 'GET',
          headers,
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });

        if (response.status === 401) {
          this.stopped = true;
          this.options.onUnauthenticated();
          break;
        }
        if (!response.ok || response.body === null) {
          throw new Error(`stream status ${response.status}`);
        }

        this.attempt = 0;
        this.options.onStatus('open');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          const result = this.parser.push(decoder.decode(value, { stream: true }));
          if (result.retryMs !== null) this.retryMs = Math.max(500, result.retryMs);
          for (const frame of result.frames) this.options.onFrame(frame);
        }
      } catch {
        // Aborts (reconnectNow / stop) and network errors take the same path.
      } finally {
        this.controller = null;
      }

      if (this.stopped) break;

      if (this.skipBackoff) {
        this.skipBackoff = false;
        continue;
      }

      this.options.onStatus(
        typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'reconnecting',
      );
      const base = Math.min(this.retryMs * 2 ** this.attempt, MAX_RETRY_MS);
      const delay = base * (0.7 + Math.random() * 0.6);
      this.attempt += 1;
      await this.wait(delay);
    }

    this.running = false;
    this.options.onStatus(this.stopped ? 'idle' : 'offline');
  }
}
